import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Select, Divider } from 'antd';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import VerifyCompany from './verifyCompany';
import { useAuth } from '../../../../context/AuthProvider';
// import { keycloakLoginUserSuccess } from '../../../../redux/actions'
import SecLogo from '../../../../utility/unnamed.png';

const { Option } = Select;

export default function SelectRole() {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);

  const keycloakAuthState = useSelector((state) => state.keycloakAuthUser);
  const keycloakData = keycloakAuthState?.data;
  const { switchRole } = useAuth(); // Get the new function
  const dispatch = useDispatch();

  const roles = useMemo(() => {
    const stored = JSON.parse(localStorage.getItem('multiple_roles'));

    if (stored && Array.isArray(stored) && stored.length > 0) {
      return stored;
    }

    if (Array.isArray(keycloakData?.user)) {
      return keycloakData.user;
    }

    if (Array.isArray(keycloakData)) {
      return keycloakData;
    }

    return [];
  }, [keycloakData]);

  useEffect(() => {
    if (!roles.length) return;

    const hasResearcher = roles.some(
      (r) => r.role_name?.toLowerCase() === 'researcher'
    );

    if (hasResearcher) {
      localStorage.setItem('user_role', 'Company');
      navigate('/company');
    }
  }, [roles, navigate]);

  const handleContinue = () => {
    if (!selectedRole) return;

    const roleObject = roles.find(
      (r) => String(r.user_id) === String(selectedRole)
    );

    if (!roleObject) return;

    switchRole(roleObject);

    const roleName = roleObject.role_name;
    if (roleName === 'Company') navigate('/company');
    else if (roleName === 'Filer') navigate('/filer');
    else if (roleName === 'ERMD') navigate('/ermd');
    else if (roleName === 'SOD') navigate('/sod');
    else navigate('/');
  };

  const handleVerified = (data) => {
    navigate("/enroll-company", { state: data });
  };

  const handleEnrollFiler = () => {
    navigate('/enroll-filer', {
      state: {
        keycloakProfile: keycloakData || {},
        companyId: localStorage.getItem('user_company') || null
      }
    });
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center overflow-hidden">
      <div className="absolute top-14 left-10 w-20 h-20 bg-green-200 rounded-full animate-bounce opacity-30" />
      <div className="absolute bottom-20 right-20 w-24 h-24 bg-green-300 rounded-full animate-pulse opacity-20" />
      <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-green-100 rounded-full animate-spin-slow opacity-25" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-8"
      >
        <div className="flex flex-col items-center mb-6">
          <img src={SecLogo} alt="SEC Logo" className="w-32 h-auto mb-4" />
          <h1 className="text-2xl font-bold text-primary-500 text-center">
            Electronic Filing and Submission Tool (eFAST)
          </h1>
          <p className="text-tertiary text-sm text-center mt-1">
            Formerly known as Online Submission Tool (OST)
          </p>
        </div>

        <Card bordered={false} className="shadow-none">
          <h2 className="text-xl font-semibold text-gray-700 mb-4 text-center">
            Select Your Role
          </h2>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Select
              placeholder="Choose your role"
              size="large"
              className="w-full mb-4"
              value={selectedRole}
              onChange={(value) => setSelectedRole(value)}
            >
              {roles.length > 0 ? (
                roles.map((r, i) => (
                  <Option key={r.user_id} value={r.user_id}>
                    {r.role_name} —{' '}
                    <span className="text-gray-500">
                      {r.company_name || r.user_name || 'No name'}
                    </span>
                  </Option>
                ))
              ) : (
                <>
                  <Option value="company">Company</Option>
                  <Option value="filer">Filer</Option>
                  <Option value="ermd">ERMD</Option>
                  <Option value="sod">SOD</Option>
                </>
              )}
            </Select>
          </motion.div>

          <Button
            type="primary"
            size="large"
            block
            className="bg-secondary text-white rounded-lg mt-2"
            disabled={!selectedRole}
            onClick={handleContinue}
          >
            Continue
          </Button>

          <Divider className="my-4">
            <span className="px-2 text-gray-500 text-sm">OR</span>
          </Divider>

          <div className="flex gap-4">
            <Button
              type="default"
              block
              className="flex-1 bg-secondary text-white rounded-lg"
              onClick={() => setVerifyModalOpen(true)}
            >
              Enroll Company
            </Button>

            <VerifyCompany
              open={verifyModalOpen}
              onClose={() => setVerifyModalOpen(false)}
              onVerified={handleVerified}
            />

            <Button
              type="default"
              block
              className="flex-1 bg-secondary text-white rounded-lg"
              onClick={handleEnrollFiler}
            >
              Enroll Filer
            </Button>
          </div>
        </Card>

        <div className="mt-8 text-center text-sm space-y-2">
          <Link
            to="/register"
            className="text-green-600 hover:text-green-700 hover:underline block"
          >
            Don’t have an account? <b>Register now to eSECURE</b>
          </Link>
          <Link to="/resend-validation" className="hover:underline block">
            Did not receive validation email?
          </Link>
          <Link to="/temporary-credentials" className="hover:underline block">
            Did not receive eFAST temporary credentials?
          </Link>
        </div>
      </motion.div>
    </div>
  );
}